import { useState } from "react";
import { API_URL } from "../../config";
import { authFetch } from "../../utils";
import { PRESET_PREEMERGENCIAS, NIVEL_COLOR, presetLabel } from "./alerpatPresets";
import logoAlerpat from "../../assets/logo_alerpat.png";
import AlerpatHistoryModal from "./AlerpatHistoryModal";

// Paleta corporativa del Ajuntament de Paterna (extraída de su escudo).
const BRAND_GREY = "#54565a";
const BRAND_GOLD = "#f5a706";

const NIVEL_BG = {
  importante: "#fff7ed",
  extraordinario: "#fef2f2",
};

export default function PreemergenciaFormPage({ onLogout }) {
  const [selectedPresets, setSelectedPresets] = useState([]);
  const [useCustom, setUseCustom] = useState(false);
  const [customTitle, setCustomTitle] = useState("");
  const [customText, setCustomText] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const togglePreset = (p) => {
    setResult(null);
    setUseCustom(false);
    setSelectedPresets((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
    );
  };

  const canSend =
    !sending &&
    (useCustom ? customTitle.trim() && customText.trim() : selectedPresets.length > 0);

  const handleSend = async () => {
    if (!canSend) return;
    const titulo = useCustom
      ? customTitle.trim()
      : selectedPresets.map((p) => presetLabel(p)).join(" · ");
    const texto = useCustom
      ? customText.trim()
      : selectedPresets.length > 1
        // Prefix each description with its own label only when combining
        // several — with just one selected, the label is already the
        // título, so repeating it here would duplicate it in the message.
        ? selectedPresets.map((p) => `${presetLabel(p)}\n${p.texto}`).join("\n\n")
        : selectedPresets.map((p) => p.texto).join("\n\n");

    const confirmed = window.confirm(`¿Seguro que quieres enviar esta preemergencia?\n\n"${titulo}"`);
    if (!confirmed) return;

    setSending(true);
    setResult(null);
    try {
      const res = await authFetch(`${API_URL}alerpat/alert`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ titulo, texto, type: "preemergencia" }),
      });
      const data = await res.json();
      setResult(data);
    } catch (err) {
      setResult({ error: err.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #f4f4f5 0%, #fdf6e8 100%)",
        padding: "1.5rem",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 640,
          background: "#fff",
          borderRadius: 18,
          padding: "2.25rem",
          boxShadow: "0 10px 35px rgba(84, 86, 90, 0.15)",
          borderTop: `4px solid ${BRAND_GOLD}`,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <img src={logoAlerpat} alt="AlerPAT" style={{ height: 44 }} />
            <h1 style={{ margin: 0, fontSize: 24, color: BRAND_GREY, fontWeight: 700 }}>AlerPAT</h1>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <button
              onClick={() => setShowHistory(true)}
              style={{ border: "1px solid #ccc", borderRadius: 4, background: "#fff", cursor: "pointer", fontSize: 12, padding: "5px 10px" }}
            >
              Histórico
            </button>
            <button
              onClick={onLogout}
              style={{ border: "none", background: "none", color: "#999", fontSize: 14, cursor: "pointer" }}
            >
              Cerrar sesión
            </button>
          </div>
        </div>
        <div style={{ fontSize: 15, color: BRAND_GOLD, fontWeight: 700, marginBottom: 26, marginLeft: 56 }}>
          Enviar preemergencia
        </div>

        {showHistory && <AlerpatHistoryModal restricted onClose={() => setShowHistory(false)} />}

        <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 4 }}>
          Recibirán la notificación todos los usuarios que tengan la app de AlerPAT instalada.
        </div>
        <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 16 }}>
          Puedes seleccionar varias; el aviso combinará todas las seleccionadas.
        </div>

        {PRESET_PREEMERGENCIAS.map((p, i) => {
          const selected = !useCustom && selectedPresets.includes(p);
          return (
            <div key={i}>
              {p.categoria && p.categoria !== PRESET_PREEMERGENCIAS[i - 1]?.categoria && (
                <div style={categoryHeaderStyle}>{p.categoria}</div>
              )}
              <label
                style={{
                  ...presetCardStyle,
                  borderColor: selected ? NIVEL_COLOR[p.nivel] || "#b45309" : "#e5e7eb",
                  background: selected ? NIVEL_BG[p.nivel] || "#fffbeb" : "#fff",
                }}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => togglePreset(p)}
                  style={{
                    marginTop: 3,
                    flexShrink: 0,
                    width: 20,
                    height: 20,
                    cursor: "pointer",
                    accentColor: NIVEL_COLOR[p.nivel] || "#b45309",
                  }}
                />
                <div>
                  <div style={{ fontWeight: "bold", fontSize: 16, display: "flex", alignItems: "center", gap: 8, color: BRAND_GREY }}>
                    {p.icon && <span>{p.icon}</span>}
                    {p.nivel && (
                      <span
                        style={{
                          display: "inline-block",
                          width: 11,
                          height: 11,
                          borderRadius: "50%",
                          backgroundColor: NIVEL_COLOR[p.nivel],
                          flexShrink: 0,
                        }}
                      />
                    )}
                    {p.titulo}
                  </div>
                  {selected && (
                    <div style={{ fontSize: 14, color: "#57534e", marginTop: 6, lineHeight: 1.5 }}>{p.texto}</div>
                  )}
                </div>
              </label>
            </div>
          );
        })}

        <label
          style={{
            ...presetCardStyle,
            marginTop: 16,
            borderColor: useCustom ? BRAND_GOLD : "#e5e7eb",
            background: useCustom ? "#fdf6e8" : "#fff",
          }}
        >
          <input
            type="radio"
            name="custom"
            checked={useCustom}
            onChange={() => {
              setUseCustom(true);
              setSelectedPresets([]);
              setResult(null);
            }}
            style={{ marginTop: 3, flexShrink: 0, width: 20, height: 20, cursor: "pointer", accentColor: BRAND_GOLD }}
          />
          <span style={{ fontSize: 16, fontWeight: "bold", color: BRAND_GREY }}>Texto libre</span>
        </label>

        {useCustom && (
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
            <input
              placeholder="Título"
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
              style={inputStyle}
            />
            <textarea
              placeholder="Mensaje"
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              rows={4}
              style={{ ...inputStyle, resize: "vertical" }}
            />
          </div>
        )}

        <button
          onClick={handleSend}
          disabled={!canSend}
          style={{
            width: "100%",
            marginTop: 30,
            padding: "18px 20px",
            background: `linear-gradient(135deg, ${BRAND_GOLD}, #c98400)`,
            color: BRAND_GREY,
            border: "none",
            borderRadius: 10,
            fontSize: 19,
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            boxShadow: canSend ? "0 6px 16px rgba(245, 167, 6, 0.4)" : "none",
            opacity: canSend ? 1 : 0.45,
            cursor: canSend ? "pointer" : "not-allowed",
            transition: "opacity 0.15s, box-shadow 0.15s",
          }}
        >
          {sending ? "Enviando…" : "Enviar preemergencia"}
        </button>

        {result && (
          <div
            style={{
              marginTop: 18,
              fontSize: 14,
              padding: "12px 16px",
              borderRadius: 8,
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              ...(result.error
                ? { background: "#fef2f2", color: "#991b1b" }
                : result.message
                ? { background: "#f3f4f6", color: "#555" }
                : { background: "#f0fdf4", color: "#166534" }),
            }}
          >
            {result.error ? (
              <>
                <span>⚠️</span>
                <span>Error: {result.error}</span>
              </>
            ) : result.message ? (
              <>
                <span>ℹ️</span>
                <span>{result.message}</span>
              </>
            ) : (
              <>
                <span>✅</span>
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span>Usuarios registrados en la app: {result.totalDevices}</span>
                  <span>
                    Envíos exitosos: {result.success}
                    {result.failure > 0 ? ` (${result.failure} fallido(s))` : ""}
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const presetCardStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: 14,
  marginBottom: 10,
  padding: "12px 14px",
  borderRadius: 10,
  border: "1.5px solid #e5e7eb",
  cursor: "pointer",
  transition: "background 0.15s, border-color 0.15s",
};

const categoryHeaderStyle = {
  fontSize: 12,
  fontWeight: "bold",
  color: "#9a9a9e",
  textTransform: "uppercase",
  letterSpacing: 0.6,
  marginTop: 22,
  marginBottom: 8,
  paddingTop: 14,
  borderTop: "1px solid #f3f4f6",
};

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  boxSizing: "border-box",
  border: "1px solid #ccc",
  borderRadius: 8,
  fontSize: 16,
};
